<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="emit('update:isVisible', false)" size="medium">
    <template #content>
      <!-- Send to creators form -->
      <div class="send-to-creators-form">
        <!-- Text part section -->
        <div class="text-part-section send-to-creators-form__text-part-section">
          <h1 class="global-h1" style="margin-bottom: 10px">
            {{ props.createdForCreatorId ? `Ready to send this to ${props.creatorFirstName ?? "the creator"}?` : "Ready to send this out to Creators?" }}
          </h1>

          <template v-if="props.createdForCreatorId">
            <p class="global-p">This will notify the creator you've created this visit for them.</p>

            <p class="global-p">They can then confirm and reach out with questions.</p>
          </template>

          <template v-else>
            <p class="global-p">This will post the opportunity to Shrpa's creator community.</p>

            <p class="global-p">Please ensure it has the details you want to share with the creators.</p>
          </template>
        </div>
        <!-- / Text part section -->

        <div class="send-to-creators-form__divider"></div>

        <ChooseCreatorDateForm
          class="send-to-creators-form__when-can-you-choose-form"
          :chooseCreatorEstimatedDate="props.chooseCreatorEstimatedDate"
          :chooseCreatorEstimatedNotes="props.chooseCreatorEstimatedNotes"
          @update:chooseCreatorEstimatedDate="newValue => emit('update:chooseCreatorEstimatedDate', newValue)"
          @update:chooseCreatorEstimatedNotes="newValue => emit('update:chooseCreatorEstimatedNotes', newValue)"
        />

        <NoteWithIcon v-if="!isDateValid" class="send-to-creators-form__error-message" size="small" type="error">
          <template #text>Please select a future date.</template>
        </NoteWithIcon>
      </div>
      <!-- / Send to creators form -->
    </template>

    <template #footer="{ closeModal }">
      <SrpButton @click="closeModal" color="gray" fill="outlined">Not yet</SrpButton>

      <SrpButton
        @click="
          () => {
            emit('sendToCreators');
          }
        "
        :isDisabled="props.saveStatus != null || !isDateValid"
      >
        <template v-if="props.saveStatus != null" #icon><LoadingIcon :size="22" /></template>
        {{ props.createdForCreatorId ? `Yes, Send to ${props.creatorFirstName}` : "Yes, Send to Creators!" }}
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed } from "vue";
import moment from "moment";

// Components
import SrpButton from "@components/ui/SrpButton.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import ChooseCreatorDateForm from "./ChooseCreatorDateForm.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    saveStatus: string | null;
    createdForCreatorId: string | null;
    creatorFirstName: string | null;
    chooseCreatorEstimatedDate: string | null;
    chooseCreatorEstimatedNotes: string | null;
  }>(),
  {
    isVisible: false,
    saveStatus: null,
    createdForCreatorId: null,
    creatorFirstName: null,
    chooseCreatorEstimatedDate: null,
    chooseCreatorEstimatedNotes: null,
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", value: false): void;
  (e: "update:chooseCreatorEstimatedDate", value: string): void;
  (e: "update:chooseCreatorEstimatedNotes", value: string): void;
  (e: "sendToCreators"): void;
}>();

// Validation
const isDateValid = computed<boolean>(() => {
  if (!props.chooseCreatorEstimatedDate) {
    return false;
  }
  const selectedDate = moment(props.chooseCreatorEstimatedDate);
  const today = moment().startOf("day");
  return selectedDate.isSameOrAfter(today, "day");
});
</script>

<style scoped lang="scss">
// Text part section ==========================================================
.text-part-section {
  content: "";
}

// Send to creators form ======================================================
.send-to-creators-form {
  &__text-part-section {
    margin-bottom: 25px;
  }

  &__divider {
    margin-bottom: 25px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__when-can-you-choose-form {
    content: "";
  }

  &__error-message {
    margin-top: 15px;
  }
}
</style>
